import { motion } from 'framer-motion'
import { useNavigate } from '@tanstack/react-router'
import { Modal } from '../ui/modal'
import { Button } from '../ui/button'
import { CheckCircle, AlertCircle, ArrowRight, CreditCard } from 'lucide-react'
import type { Service } from '../../models/service'
import { useServices } from '@/state/services'
import { useMutationTracker } from '@/hooks/useMutationTracker'

interface ServicePurchaseModalProps {
  isOpen: boolean
  onClose: () => void
  service: Service | null
}

export function ServicePurchaseModal({ isOpen, onClose, service }: ServicePurchaseModalProps) {
  const navigate = useNavigate()

  const {initPurchase} = useServices()

  const initTracker = useMutationTracker('init-purchase')

  initTracker.addListener(({ isSuccess, data }) => {
    if (isSuccess && data && typeof data === 'object' && 'id' in data) {
      console.log('Purchase initialized with data:', data);
      
      navigate({ to: '/dashboard/services/confirm-purchase', search: { transaction_id: (data as { id: string }).id } })
      onClose()
    }
  })



  const handlePurchase = () => {
    if (service) {
      initPurchase({service_id: service.id})
    }
  }

  const handleClose = () => {
    onClose()
  }

  if (!service) return null

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Purchase ${service.name}`}>
      <div className="space-y-6">
        {!initTracker.isPending && !initTracker.isSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            {/* Service Info */}
            <div className="space-y-4">
              <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  {service.name}
                </h3>
              </div>
              
              {/* Description Section - Prominently Displayed */}
              <div className="bg-muted/50 rounded-xl p-4 border border-border max-h-48 overflow-y-auto">
                <div 
                  className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: service.description }}
                />
              </div>
              
              <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                ${service.price}
              </div>
            </div>

            {/* Error Message */}
            {Boolean(initTracker.error) && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="text-sm font-medium text-red-900 dark:text-red-100">
                    Purchase Failed
                  </span>
                </div>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {'Either you have a pending purchase or an error occurred.'}
                </p>
              </motion.div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={handlePurchase} 
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold"
                disabled={initTracker.isPending}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Initialize Purchase
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={handleClose} className="w-full border-border text-foreground hover:bg-muted">
                Cancel
              </Button>
            </div>

            <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
              You'll be redirected to complete the payment process
            </div>
          </motion.div>
        )}

        {initTracker.isPending && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-6 py-8"
          >
            <div className="relative mx-auto w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-muted"></div>
              <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                Initializing Purchase
              </h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we prepare your transaction...
              </p>
            </div>
          </motion.div>
        )}

        {initTracker.isSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-4"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <div>
              <h3 className="text-lg font-semibold text-green-600">
                Purchase Initialized!
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Redirecting to payment confirmation...
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </Modal>
  )
}
